import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const Reports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [revenueBreakdown, setRevenueBreakdown] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    loadReports();
  }, [selectedPeriod, startDate, endDate, useCustomDates]);

  const loadReports = async () => {
    setLoading(true);
    setError('');
    try {
      const period = useCustomDates ? null : selectedPeriod;
      const customStartDate = useCustomDates ? startDate : null;
      const customEndDate = useCustomDates ? endDate : null;

      const [accountingData, revenueData] = await Promise.all([
        api.getAccountingReports(period, customStartDate, customEndDate),
        api.getRevenueBreakdown(period, customStartDate, customEndDate)
      ]);

      if (accountingData.success) {
        setReportData(accountingData.data);
      }
      if (revenueData.success) {
        setRevenueBreakdown(revenueData.data);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setUseCustomDates(false);
    setStartDate('');
    setEndDate('');
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setError('Start date must be before end date');
        return;
      }
      setUseCustomDates(true);
      setError('');
    } else {
      setUseCustomDates(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Accounting Reports</h3>
              <div className="card-tools">
                <div className="row align-items-center">
                  <div className="col-auto">
                    <div className="btn-group">
                      <button
                        type="button"
                        className={`btn btn-sm ${!useCustomDates && selectedPeriod === 'weekly' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => handlePeriodChange('weekly')}
                      >
                        Weekly
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${!useCustomDates && selectedPeriod === 'monthly' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => handlePeriodChange('monthly')}
                      >
                        Monthly
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${!useCustomDates && selectedPeriod === 'quarterly' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => handlePeriodChange('quarterly')}
                      >
                        Quarterly
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${!useCustomDates && selectedPeriod === 'yearly' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => handlePeriodChange('yearly')}
                      >
                        Yearly
                      </button>
                    </div>
                  </div>
                  <div className="col-auto ml-3">
                    <div className="input-group input-group-sm">
                      <div className="input-group-prepend">
                        <span className="input-group-text">From</span>
                      </div>
                      <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (e.target.value && endDate) {
                            handleCustomDateChange();
                          }
                        }}
                        style={{ maxWidth: '150px' }}
                      />
                      <div className="input-group-prepend">
                        <span className="input-group-text">To</span>
                      </div>
                      <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          if (startDate && e.target.value) {
                            handleCustomDateChange();
                          }
                        }}
                        style={{ maxWidth: '150px' }}
                      />
                      {(startDate || endDate) && (
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="btn btn-default"
                            onClick={() => {
                              setStartDate('');
                              setEndDate('');
                              setUseCustomDates(false);
                            }}
                            title="Clear dates"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="text-center p-4">
                  <i className="fas fa-spinner fa-spin fa-2x"></i>
                  <p className="mt-2">Loading reports...</p>
                </div>
              ) : reportData ? (
                <>
                  {/* Summary Cards */}
                  <div className="row mb-4">
                    <div className="col-lg-3 col-6">
                      <div className="small-box bg-info">
                        <div className="inner">
                          <h3>{formatNumber(reportData.licenses.total)}</h3>
                          <p>Total Licenses</p>
                        </div>
                        <div className="icon">
                          <i className="fas fa-key"></i>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-6">
                      <div className="small-box bg-success">
                        <div className="inner">
                          <h3>{formatCurrency(reportData.revenue.total)}</h3>
                          <p>Total Revenue</p>
                        </div>
                        <div className="icon">
                          <i className="fas fa-dollar-sign"></i>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-6">
                      <div className="small-box bg-warning">
                        <div className="inner">
                          <h3>{formatNumber(reportData.requests.total)}</h3>
                          <p>Plan Requests</p>
                        </div>
                        <div className="icon">
                          <i className="fas fa-clipboard-list"></i>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-6">
                      <div className="small-box bg-danger">
                        <div className="inner">
                          <h3>{formatNumber(reportData.tickets.total)}</h3>
                          <p>Support Tickets</p>
                        </div>
                        <div className="icon">
                          <i className="fas fa-ticket-alt"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Information */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card card-primary">
                        <div className="card-header">
                          <h3 className="card-title">License Statistics</h3>
                        </div>
                        <div className="card-body">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <td><strong>Active Licenses</strong></td>
                                <td className="text-right">{formatNumber(reportData.licenses.active)}</td>
                              </tr>
                              <tr>
                                <td><strong>Expired Licenses</strong></td>
                                <td className="text-right">{formatNumber(reportData.licenses.expired)}</td>
                              </tr>
                              <tr>
                                <td><strong>Expiring Licenses</strong></td>
                                <td className="text-right">{formatNumber(reportData.licenses.expiring)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card card-success">
                        <div className="card-header">
                          <h3 className="card-title">Revenue Breakdown</h3>
                        </div>
                        <div className="card-body">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <td><strong>Monthly Revenue</strong></td>
                                <td className="text-right">{formatCurrency(reportData.revenue.monthly)}</td>
                              </tr>
                              <tr>
                                <td><strong>Yearly Revenue</strong></td>
                                <td className="text-right">{formatCurrency(reportData.revenue.yearly)}</td>
                              </tr>
                              <tr>
                                <td><strong>Potential Revenue</strong></td>
                                <td className="text-right">{formatCurrency(reportData.revenue.potential)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Revenue by Tier */}
                  {revenueBreakdown.length > 0 && (
                    <div className="row mt-4">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header">
                            <h3 className="card-title">Revenue by License Tier</h3>
                          </div>
                          <div className="card-body">
                            <table className="table table-striped">
                              <thead>
                                <tr>
                                  <th>Tier Name</th>
                                  <th className="text-right">Licenses</th>
                                  <th className="text-right">Monthly Revenue</th>
                                  <th className="text-right">Yearly Revenue</th>
                                  <th className="text-right">Total Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {revenueBreakdown.map((tier, index) => (
                                  <tr key={index}>
                                    <td><strong>{tier.tierName}</strong></td>
                                    <td className="text-right">{formatNumber(tier.licenseCount)}</td>
                                    <td className="text-right">{formatCurrency(tier.monthlyRevenue)}</td>
                                    <td className="text-right">{formatCurrency(tier.yearlyRevenue)}</td>
                                    <td className="text-right"><strong>{formatCurrency(tier.totalRevenue)}</strong></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Plan Requests & Tickets */}
                  <div className="row mt-4">
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h3 className="card-title">Plan Requests</h3>
                        </div>
                        <div className="card-body">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <td><strong>Pending</strong></td>
                                <td className="text-right">{formatNumber(reportData.requests.pending)}</td>
                              </tr>
                              <tr>
                                <td><strong>Approved</strong></td>
                                <td className="text-right">{formatNumber(reportData.requests.approved)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h3 className="card-title">Support Tickets</h3>
                        </div>
                        <div className="card-body">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <td><strong>Open</strong></td>
                                <td className="text-right">{formatNumber(reportData.tickets.open)}</td>
                              </tr>
                              <tr>
                                <td><strong>Resolved</strong></td>
                                <td className="text-right">{formatNumber(reportData.tickets.resolved)}</td>
                              </tr>
                              <tr>
                                <td><strong>Closed</strong></td>
                                <td className="text-right">{formatNumber(reportData.tickets.closed)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date Range Info */}
                  {reportData.dateRange && (
                    <div className="row mt-4">
                      <div className="col-12">
                        <div className="alert alert-info">
                          <strong>Report Period:</strong> {formatDate(reportData.dateRange.start)} - {formatDate(reportData.dateRange.end)}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;

